import { inject, Injectable, type Signal, signal } from '@angular/core';
import { getToken, Messaging } from '@angular/fire/messaging';
import { SwPush } from '@angular/service-worker';
import { DOCUMENT } from '@angular/common';
import type { Observable } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import { environment } from '@szebit-app/environment';
import { assertIsNotNil } from '@pct-ts/assert';

export type PushNotificationStatus = 'NOT_SUPPORTED' | 'SUPPORTED' | 'SUBSCRIBED';

@Injectable({
  providedIn: 'root',
})
export class PushNotificationService {
  readonly #document = inject(DOCUMENT);
  readonly #messaging = inject(Messaging);
  readonly #swPush = inject(SwPush);
  readonly #status = signal<PushNotificationStatus>('SUPPORTED');
  get status(): Signal<PushNotificationStatus> {
    return this.#status.asReadonly();
  }
  get status$(): Observable<PushNotificationStatus> {
    return toObservable(this.#status);
  }
  readonly #subscriptions = signal<PushSubscription | null>(null);

  constructor() {
    if (!this.#swPush.isEnabled) {
      this.#status.set('NOT_SUPPORTED');
      return;
    }

    // Meglévő subscription ellenőrzése
    this.#swPush.subscription.subscribe(subscription => {
      if (subscription !== null) {
        this.#status.set('SUBSCRIBED');
        this.#subscriptions.set(subscription);
      }
    });
  }

  async subscribe(): Promise<PushSubscription | 'HAS_NOT_PERMISSION' | null> {
    if (!this.#swPush.isEnabled) {
      return null;
    }

    try {
      // Notification engedély kérése
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        return 'HAS_NOT_PERMISSION';
      }
      // Feliratkozás Web Push-ra a VAPID kulccsal
      const vapidKey = environment.vapidKey;
      assertIsNotNil(vapidKey, 'VAPID key is not defined');
      const subscription = await this.#swPush.requestSubscription({
        serverPublicKey: vapidKey,
      });
      this.#status.set('SUBSCRIBED');

      return subscription;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async unsubscribeFromPush(): Promise<boolean> {
    if (!this.#swPush.isEnabled) {
      return false;
    }

    try {
      await this.#swPush.unsubscribe();
      this.#status.set('SUPPORTED');
      this.#subscriptions.set(null);
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  getFcmToken(): Promise<string | null> {
    if (this.#status() !== 'SUBSCRIBED') {
      return Promise.resolve(null);
    }

    return (
      this.#document.defaultView?.navigator.serviceWorker.ready.then(
        async serviceWorkerRegistration => {
          const vapidKey = environment.vapidKey;
          assertIsNotNil(vapidKey, 'VAPID key is not defined');
          const token = await getToken(this.#messaging, {
            vapidKey,
            serviceWorkerRegistration,
          });
          return token;
        },
      ) ?? Promise.resolve(null)
    );
  }
}
